import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { LinkedinAccount } from './linkedin-accounts.types';
import { LinkedinTypes } from 'enums';
interface Props {
  open: boolean;
  account: LinkedinAccount | null;
  handleClose(): void;

  handleSynchronizeLinkedinAccount(): void;
}

const TwoFaNotEnabledDialog: React.FC<Props> = ({ open, account, handleClose, handleSynchronizeLinkedinAccount }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const liProductType = account?.liProductType || LinkedinTypes.LINKEDIN;

  const liProductTypeText =
    liProductType === LinkedinTypes.RECRUITER ? 'Recruiter' : liProductType === LinkedinTypes.SALES_NAV ? 'Sales Navigator' : 'LinkedIn';

  const regularTwoFaUrl = 'https://www.linkedin.com/mypreferences/d/two-factor-authentication';
  const recruiterTwoFaUrl = 'https://www.linkedin.com/talent/two-step-verification';

  const url = liProductType === LinkedinTypes.RECRUITER ? recruiterTwoFaUrl : regularTwoFaUrl;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('syncErrorTwoFaNotEnabled')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>
            <strong>{t('syncErrorTwoFaNotEnabledText1', { liProductType: liProductTypeText })}</strong>
          </Typography>
          <Typography className={classes.text}>
            <Trans i18nKey={'syncErrorTwoFaNotEnabledText2'} values={{ liProductType: liProductTypeText }}>
              Please
              <a className={classes.link} href={url} target={'_blank'} rel={'noreferrer'}>
                activate 2FA in your {liProductTypeText} account
              </a>
              .
            </Trans>
          </Typography>
          <Typography className={classes.text}>{t('syncErrorTwoFaNotEnabledText3')}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton text={t('retrySynchronization')} size={'small'} border={'rounded'} action={handleSynchronizeLinkedinAccount} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
    '&.highlight': {
      color: theme.palette.primary.main,
    },
  },
  link: {
    display: 'inline',
    fontSize: theme.typography.pxToRem(15),
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
    textAlign: 'left',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default TwoFaNotEnabledDialog;
